import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import FormItem from '../../../components/Form/FormItem';
import FieldSet from '../../../components/Form/FieldSet';
import Label from '../../../components/Form/Label';
import Input from '../../../components/Form/Input';
import Title from '../../../components/Title';
import Select from '../../../components/Form/Select';
import Button from '../../../components/Button';
import { UserData } from '../../../types/userData';
import inputMask from '../../../util/mask';
import { useAuth } from '../../../hooks/useAuth';
import { Actions, Categories } from '../../../types/gtm';
import DocumentForm from '../DocumentForm/DocumentForm';
import { LinkWithRoleButton } from '../../../components/Link/LinkWithRoleButton';
import Modal from '../../../components/Modal';

const BoxSocialName = styled.div`
  width: 366px;
  font-size: 16px;
  line-height: 21px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: 64px;
  gap: 8px;

  @media (max-width: 650px) {
    font-size: 14px;
    width: 100%;
  }
`;

const ModalContainer = styled.div`
  flex-direction: column;
  display: flex;
  width: 380px;
  margin-top: 4px;
  margin-bottom: 28px;
  font-size: 16px;
  gap: 20px;
  @media (max-width: 650px) {
    font-size: 14px;
    width: 100%;
  }
  > span {
    line-height: 150%;
  }
`;

const InputModal = styled.div`
  margin-bottom: 28px;
`;

const BoxWithButton = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #a5a5a5;
  border-bottom: 1px solid #a5a5a5;
  margin-top: 16px;
  font-size: 14px;
  font-style: normal;
  line-height: 18px;

  > button {
    align-self: center;
    font-size: 14px;
    width: 161px;
    @media (max-width: 650px) {
      width: 161px;
      align-self: flex-start;
      font-size: 12px;
    }
  }
  padding-top: 16px;
  padding-bottom: 16px;

  > div > p:first-child {
    color: #666666;
    margin: 0px 0 4px 0;
  }
  > div > p {
    margin: 0px;
  }

  &:last-child {
    border-top: none;
  }

  @media (max-width: 650px) {
    width: 100%;
    align-items: flex-start;
  }
`;

export interface Props {
  title?: string;
  mobile?: boolean;
  onDocNumberConfirmResult?: () => void;
  onDocNumberInconsistentResult?: () => void;
}

const RegistrationDataForm = ({
  title,
  mobile,
  onDocNumberConfirmResult,
  onDocNumberInconsistentResult,
}: Props) => {
  const {
    values,
    handleChange,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<UserData>();
  const { user } = useAuth();
  const history = useHistory();
  const [fullNameFocused, setFullNameFocused] = useState<boolean>(false);
  const [birthDateFocused, setBirthDateFocused] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localSocialName, setLocalSocialName] = useState(
    values.socialName || '',
  );
  const [isNameConfirmed, setIsNameConfirmed] = useState(false);

  const checkChars = (str: string) => {
    const max = 22;
    return str.length > max ? `${str.substring(0, max)}...` : str;
  };

  const secondText =
    values && values.isDocNumberLocked
      ? 'Você não pode editar essa informação'
      : '';

  const generateErrorMessage = (
    userData: UserData,
    field: string,
    error?: string,
  ) => {
    if (error) {
      return error;
    }

    if (userData.hasInconsistentData) {
      switch (field) {
        case 'fullName':
          return fullNameFocused ? '' : 'Você deve informar um nome válido.';
        case 'birthDate':
          return birthDateFocused
            ? ''
            : 'Você deve informar uma data de nascimento válida.';
        default:
          return '';
      }
    }

    return '';
  };

  const onInconsistentResult = () => {
    setFullNameFocused(false);
    setBirthDateFocused(false);
    if (onDocNumberInconsistentResult) {
      onDocNumberInconsistentResult();
    }
  };

  const getErrorMessage = () => {
    return errors.socialName && touched.socialName ? errors.socialName : '';
  };

  const openModal = () => {
    setLocalSocialName(values.socialName || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setLocalSocialName(values.socialName || '');
    setIsModalOpen(false);
  };

  const titleModal = () => {
    return isNameConfirmed ? 'Alterar nome social' : 'Adicione seu nome social';
  };

  const textBodyModal = () => {
    const baseText = (
      <span>
        Nome social é o nome que pessoas transgênero, travestis, trans e
        não-binárias escolhem ser chamadas.
      </span>
    );
    const extraText = isNameConfirmed ? (
      <span>
        Após realizar essa alteração, a Globo passará a lhe chamar pelo novo
        Nome Social escolhido por você.
      </span>
    ) : null;
    return (
      <>
        {baseText}
        {extraText}
      </>
    );
  };

  const insertSocialName = () => {
    if (generateErrorMessage !== errors) {
      setFieldValue('socialName', localSocialName);
      setFieldTouched('socialName', true);
      setIsNameConfirmed(true);
      closeModal();
    }
  };

  const handleSocialNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSocialName = e.target.value;
    const socialNameInput = newSocialName.trim().split(/\s+/).length;
    if (socialNameInput !== 0 || socialNameInput <= 2) {
      setLocalSocialName(newSocialName);
    }
  };

  return (
    <FormItem>
      {title && <Title>{title}</Title>}
      <FieldSet>
        <Label htmlFor="fullName">Nome completo</Label>
        <Input
          id="fullName"
          readOnly={values.isDocNumberLocked}
          value={values.fullName}
          type="text"
          name="fullName"
          onChange={e => {
            handleChange(e);
          }}
          aria-label={`${secondText}`}
          placeHolderText="Digite o seu nome completo"
          onClickCapture={() => setFullNameFocused(true)}
          errorMessage={generateErrorMessage(
            values,
            'fullName',
            errors.fullName,
          )}
        />
        <BoxWithButton>
          <div>
            <p>Email</p>
            <p>{checkChars(values.email || '')}</p>
          </div>
          <Button
            onClick={() => {
              window.dataLayer.push({
                event: 'customEventUserInteraction',
                event_category: Categories.EditUserPersonalData,
                event_action: Actions.ClickEditEmail,
                bd_suser_code: user?.globoId,
              });
              history.push('/email/change');
            }}
            variant="secondary"
          >
            {mobile ? 'Alterar' : 'Alterar E-mail'}
          </Button>
        </BoxWithButton>
        <BoxWithButton>
          <div>
            <p>Sua senha</p>
            <p aria-hidden>**************</p>
          </div>
          <Button
            onClick={() => {
              window.dataLayer.push({
                event: 'customEventUserInteraction',
                event_category: Categories.EditUserPersonalData,
                event_action: Actions.ClickEditPassword,
                bd_suser_code: user?.globoId,
              });
              history.push('/alterar-senha');
            }}
            variant="secondary"
          >
            {mobile ? 'Alterar' : 'Alterar Senha'}
          </Button>
        </BoxWithButton>
      </FieldSet>
      <FieldSet>
        <Label htmlFor="birthDate">Data de nascimento</Label>
        <Input
          id="birthDate"
          aria-required="true"
          readOnly={values.isDocNumberLocked}
          value={inputMask.date(values.birthDate)}
          onChange={e => {
            handleChange(e);
          }}
          type="text"
          name="birthDate"
          aria-label={`${secondText}`}
          placeHolderText="Informe a sua data de nascimento"
          onClickCapture={() => setBirthDateFocused(true)}
          errorMessage={generateErrorMessage(
            values,
            'birthDate',
            errors.birthDate,
          )}
        />
      </FieldSet>
      <FieldSet>
        <DocumentForm
          title=""
          user={user}
          isIntervention={false}
          isInterventionFlow={Boolean(!onInconsistentResult)}
          width="100%"
          onConfirmResult={onDocNumberConfirmResult}
          onInconsistentResult={onInconsistentResult}
        />
      </FieldSet>
      {values.siscoserv?.nif && (
        <FieldSet>
          <Label htmlFor="nif">Documento internacional</Label>
          <Input
            id="nif"
            readOnly
            value={values.siscoserv?.nif}
            onChange={handleChange}
            name="siscoserv.nif"
            aria-label={`${secondText}`}
          />
        </FieldSet>
      )}
      <FieldSet>
        <Label htmlFor="gender">Gênero</Label>
        <Select
          aria-required="true"
          id="gender"
          onChange={handleChange}
          value={values.gender}
          name="gender"
          errorMessage={errors.gender ? errors.gender : ''}
        >
          <option value="">Selecione um gênero</option>
          <option value="F">Feminino</option>
          <option value="M">Masculino</option>
          <option value="O">Outro</option>
          <option value="N">Não quero informar</option>
        </Select>
      </FieldSet>
      <FieldSet>
        <BoxSocialName>
          <Label htmlFor="socialName" optionalText="Opcional">
            Nome Social
          </Label>
          {values.socialName !== '' ? (
            <div>
              <span>
                Você adicionou <strong>{values.socialName}</strong> como seu
                nome social.{' '}
              </span>
              <LinkWithRoleButton tabIndex={0} onClick={openModal}>
                Deseja alterar?
              </LinkWithRoleButton>
            </div>
          ) : (
            <div>
              <LinkWithRoleButton tabIndex={0} onClick={openModal}>
                Saiba o que é o nome social
              </LinkWithRoleButton>
              <span> e escolha se deseja adicionar ao seu cadastro.</span>
            </div>
          )}
        </BoxSocialName>
        {isModalOpen && (
          <Modal
            maxWidth="432px"
            contentHeight={isNameConfirmed ? '442px' : 'auto'}
            contentWidth="100%"
            borderRadius="16px"
            customHeader={titleModal()}
            closeButton={() => closeModal()}
          >
            <ModalContainer>{textBodyModal()}</ModalContainer>
            <Label htmlFor="socialName" optionalText="Opcional">
              Nome Social
            </Label>
            <InputModal>
              <Input
                id="socialName"
                readOnly={false}
                type="text"
                name="socialName"
                width="100%"
                value={localSocialName}
                onChange={handleSocialNameChange}
                errorMessage={generateErrorMessage(
                  values,
                  'socialName',
                  errors.socialName,
                )}
                placeHolderText="Informe o seu nome social"
                style={{ height: '48px' }}
              />
            </InputModal>
            <Button
              variant="primary"
              onClick={() => insertSocialName()}
              width="100%"
              disabled={getErrorMessage() !== ''}
            >
              {isNameConfirmed ? 'Alterar' : 'Adicionar'}
            </Button>
          </Modal>
        )}
      </FieldSet>
    </FormItem>
  );
};

export default RegistrationDataForm;
